import { makeStyles } from "@mui/styles";
import {
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { Search } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment/InputAdornment";
import MaUTable from "@mui/material/Table";
import React, { useContext, useState } from "react";
import Skeleton from "@mui/material/Skeleton";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel/TableSortLabel";

import { BreakpointContext } from "contexts/breakpoint";
import { colors } from "styles/colors";
import { TableProps } from "./Table.types";
import { theme } from "styles/theme";
import Filter from "../filter/Filter";
import TextFieldInput from "../text-field-input/TextFieldInput";

const useStyles = makeStyles({
  root: {
    border: "none",
    height: (props: { height: string | undefined }) => props.height || "75vh",
    "& .MuiTableHead": {
      borderBottom: "2px solid blue",
    },
    "& .MuiTableHead-root .MuiTableRow-root": {
      height: "60px",
      background: colors.white,
      "&:hover": {
        backgroundColor: colors.white,
      },
    },
    "& .MuiTableRow-root": {
      height: (props: { rowHeight: string | undefined; height: string | undefined }) =>
        props?.rowHeight ? props.rowHeight : "100%",
      minHeight: "80px",
      "&:hover": {
        backgroundColor: colors.tableHover,
      },
    },
    "& .MuiTableCell-body": {
      display: "inline-flex !important",
      alignItems: "center",
    },
  },
  // Opt-in tighter layout: columns sized to their width instead of stretching to fill the row
  // (the last column takes the leftover space), shorter rows, and slightly heavier text.
  dense: {
    // The shared row style has an 80px min-height that also hits the header row; undo it here.
    "& .MuiTableHead-root .MuiTableRow-root": {
      height: "auto !important",
      minHeight: "40px !important",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      minHeight: "44px !important",
    },
    "& .MuiTableCell-root": {
      flexGrow: "0 !important",
      padding: "6px 12px",
    },
    "& .MuiTableCell-root:last-child": {
      flexGrow: "1 !important",
    },
    "& .MuiTableCell-head": {
      display: "inline-flex !important",
      alignItems: "center",
      paddingTop: 8,
      paddingBottom: 8,
      fontWeight: 600,
      fontSize: 13,
      color: colors.darkGrey,
      whiteSpace: "nowrap",
    },
    "& .MuiTableCell-body": {
      fontWeight: 500,
      fontSize: 14,
      color: theme.palette.text.primary,
    },
  },
});

type MovableColumn = { id: string; disableMove?: boolean };

// Varied placeholder widths so loading rows read as text, not a grid of identical bars.
const SKELETON_WIDTHS = ["72%", "48%", "86%", "60%", "40%", "78%", "54%"];

const Table = ({
  columns,
  rows,
  pageSize = 10,
  onRowClick,
  totalCount,
  onPageChange,
  pageIndex,
  prevParams,
  nextParams,
  path,
  hidePaginationControls = false,
  useCursorPagination = false,
  filters,
  setFilters,
  globalOperator,
  setGlobalOperator,
  filterFieldOptions,
  applyFilters,
  sortingSettings,
  setSortingSettings,
  searchValue,
  onSearchChange,
  tableFooter,
  tableRef,
  rowHeight,
  containerHeight,
  tableHeight,
  customSearch,
  onSortClick,
  dense = false,
  fillHeight = false,
  paginationSummary,
  loading = false,
  onColumnMove,
}: TableProps): JSX.Element => {
  const classes = useStyles({ rowHeight: rowHeight, height: tableHeight });
  const history = useHistory();
  const { isMobileView } = useContext(BreakpointContext);

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: rows,
      initialState: { pageIndex, pageSize },
      manualPagination: true,
      pageCount: -1,
      manualSortBy: true,
    },
    useGlobalFilter,
    useSortBy,
    useFlexLayout,
    usePagination,
    useRowSelect
  );

  const handleClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  // Contained layout: the rows scroll inside a bordered box that fills the space it's given,
  // with the result count and page arrows pinned underneath.
  const contained = Boolean(fillHeight);

  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [dropColumnId, setDropColumnId] = useState<string | null>(null);
  const endColumnDrag = () => {
    setDraggedColumnId(null);
    setDropColumnId(null);
  };
  const isMovable = (column: MovableColumn) =>
    Boolean(onColumnMove) && column.id !== "selection" && !column.disableMove;
  // Native drag and drop on movable headers. The drop target shows a line on the side the column lands.
  const columnDragProps = (column: MovableColumn, headers: MovableColumn[]) => {
    if (!isMovable(column)) return {};
    const indexOf = (id: string | null) => headers.findIndex((h) => h.id === id);
    const isDropTarget = dropColumnId === column.id && draggedColumnId !== column.id;
    const landsAfter = indexOf(draggedColumnId) < indexOf(column.id);
    return {
      draggable: true,
      title: "Drag to move column",
      onDragStart: (event: React.DragEvent) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", column.id);
        setDraggedColumnId(column.id);
      },
      onDragOver: (event: React.DragEvent) => {
        if (!draggedColumnId) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        if (dropColumnId !== column.id) setDropColumnId(column.id);
      },
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        if (draggedColumnId && draggedColumnId !== column.id) onColumnMove?.(draggedColumnId, column.id);
        endColumnDrag();
      },
      onDragEnd: endColumnDrag,
      sx: {
        cursor: "grab",
        opacity: draggedColumnId === column.id ? 0.5 : 1,
        boxShadow: isDropTarget
          ? `inset ${landsAfter ? -3 : 3}px 0 0 ${colors.indigo}`
          : undefined,
      },
    };
  };
  // Placeholder rows take their column widths from the bottom header row.
  const columnHeaders = headerGroups[headerGroups.length - 1];
  const paginationControls = !hidePaginationControls && (
    <TablePagination
      data-sentry-unmask
      rowsPerPageOptions={[]}
      count={totalCount}
      rowsPerPage={pageSize}
      page={pageIndex}
      labelDisplayedRows={({ from, to, count }) => {
        if (useCursorPagination || paginationSummary) return "";
        return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
      }}
      onPageChange={(e, page) => {
        if (useCursorPagination) {
          history.push({
            pathname: path,
            search: pageIndex > page ? prevParams : nextParams,
          });
        } else {
          onPageChange(page);
        }
      }}
      backIconButtonProps={
        typeof prevParams === "string"
          ? {
              disabled: prevParams === "",
            }
          : undefined
      }
      nextIconButtonProps={
        typeof nextParams === "string"
          ? {
              disabled: nextParams === "",
            }
          : undefined
      }
      sx={{
        border: "0",
        padding: "0",
      }}
    />
  );
  const hasTopControls =
    Boolean(filters && filters.length && typeof globalOperator === "number") ||
    Boolean(customSearch) ||
    Boolean(onSearchChange);

  return (
    <div
      className={dense ? `${classes.root} ${classes.dense}` : classes.root}
      style={contained ? { height: "100%", display: "flex", flexDirection: "column", minHeight: 0 } : undefined}
    >
      <Box
        sx={{
          display: contained && !hasTopControls ? "none" : "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          flexDirection: isMobileView ? "column-reverse" : "row",
          overflow: "hidden",
          paddingTop: hidePaginationControls ? "3rem" : "0",
        }}
      >
        <Box
          sx={{
            maxWidth: isMobileView ? "calc(100%-16px)" : "60%",
            width: isMobileView ? "calc(100%-16px)" : "60%",
          }}
        >
          {filters && Boolean(filters.length) && typeof globalOperator === "number" && (
            <Filter
              filters={filters}
              setFilters={setFilters}
              globalOperator={globalOperator}
              setGlobalOperator={setGlobalOperator}
              filterFieldOptions={filterFieldOptions}
              applyFilters={applyFilters}
            />
          )}
          {customSearch && <>{customSearch}</>}
          {onSearchChange && (
            <TextFieldInput
              label={""}
              value={searchValue}
              onChange={(value) => onSearchChange(value)}
              variant="standard"
              placeholder="Search..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              style={{ maxWidth: "300px", margin: theme.spacing(2) }}
            />
          )}
        </Box>
        {!contained && paginationControls}
      </Box>
      <TableContainer
        ref={tableRef}
        sx={
          contained
            ? {
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                border: `1px solid ${colors.lightGrey4}`,
                borderRadius: 1,
                backgroundColor: colors.white,
                // Rows are as wide as their columns, so narrow screens scroll sideways inside the box.
                "& .MuiTable-root": { width: "max-content", minWidth: "100%" },
                "& .MuiTableRow-root": { minWidth: "100% !important" },
              }
            : containerHeight
              ? { height: containerHeight }
              : {}
        }
      >
        <MaUTable {...getTableProps()} stickyHeader>
          {/* stickyHeader prop doesnt work due to useTable adding display flex to each column */}
          {/* instead just make Thead sticky, note maxHeight must be set */}
          <TableHead sx={{ position: "sticky", top: 0, zIndex: 2 }}>
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <TableRow
                sx={{ height: "auto" }}
                {...headerGroup.getHeaderGroupProps()}
                key={`${headerGroup.id}_${headerGroupIndex}`}
              >
                {headerGroup.headers.map((column, columnIndex) => {
                  const currentColumnSetting =
                    sortingSettings &&
                    sortingSettings.find((s) => s.replace("-", "") === column.id);

                  return (
                    <TableCell
                      {...(column.id === "selection" || onSortClick
                        ? column.getHeaderProps()
                        : column.getHeaderProps(column.getSortByToggleProps()))}
                      {...columnDragProps(column, headerGroup.headers)}
                      key={`${column.id}_${columnIndex}`}
                    >
                      {column.render("Header")}
                      {column.id !== "selection" ? (
                        <TableSortLabel
                          hideSortIcon={column.disableSortBy}
                          active={Boolean(currentColumnSetting)}
                          direction={
                            currentColumnSetting && currentColumnSetting.startsWith("-")
                              ? "desc"
                              : "asc"
                          }
                          onClick={(event: React.MouseEvent) => {
                            if (onSortClick) {
                              if (!column.disableSortBy) onSortClick(column.id, event.shiftKey);
                              return;
                            }
                            const resetURL = `${path}?page=1`;
                            history.push(resetURL);

                            if (sortBy.length === 0) {
                              setSortingSettings(["-created_at"]);
                              return;
                            }

                            const currentSetting = sortBy.find((s) => s.id === column.id);

                            if (currentSetting) {
                              const direction = currentSetting?.desc ? "desc" : "asc";
                              setSortingSettings([
                                `${direction === "desc" ? "-" : ""}${column.id}`,
                              ]);
                            } else {
                              const newSetting = sortBy[0];
                              setSortingSettings([`${newSetting.desc ? "-" : ""}${newSetting.id}`]);
                            }
                          }}
                        />
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody aria-busy={loading || undefined}>
            {loading &&
              Array.from({ length: pageSize }, (_, rowIndex) => (
                <TableRow
                  key={`skeleton_${rowIndex}`}
                  data-testid="table-skeleton-row"
                  style={columnHeaders.getHeaderGroupProps().style}
                  sx={{ cursor: "default" }}
                >
                  {columnHeaders.headers.map((column, columnIndex) => (
                    <TableCell key={`skeleton_${rowIndex}_${column.id}`} style={column.getHeaderProps().style}>
                      <Skeleton
                        variant="text"
                        width={SKELETON_WIDTHS[(rowIndex + columnIndex) % SKELETON_WIDTHS.length]}
                        sx={{ fontSize: dense ? 14 : 16 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading && page.map((row, rowIndex) => {
              prepareRow(row);
              return (
                <TableRow
                  {...row.getRowProps()}
                  key={`${row.id}_${rowIndex}`}
                  onClick={() => handleClick(row.original)}
                >
                  {row.cells.map((cell, cellIndex) => {
                    return (
                      <TableCell {...cell.getCellProps()} key={`cell_${cell.row.id}_${cellIndex}`}>
                        {cell.render("Cell")}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
          {tableFooter}
        </MaUTable>
      </TableContainer>
      {contained && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexShrink: 0,
            pt: 1,
            fontSize: 13,
            color: colors.darkGrey,
          }}
          data-sentry-unmask
        >
          <Box>{paginationSummary}</Box>
          {paginationControls}
        </Box>
      )}
    </div>
  );
};

export default Table;
